// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Body,
  Button,
  H3,
  LgPadding,
  LgSpacing,
  SmSpacing,
} from '@alto/design-system';
import React, { useContext, useEffect } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type OrderTypeContext } from '../../../index';

type Props = {
  readonly prescriptionID: number | undefined;
  readonly orderTypeContext: OrderTypeContext;
};

export const PricePendingActionSheet = ({ orderTypeContext, prescriptionID }: Props) => {
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent({
      event: EVENTS.ASSISTANT_COMPONENT_VIEWED,
      params: {
        name: 'PricePendingActionSheet',
      },
      additionalFields: { prescriptionId: prescriptionID },
    });
  }, [trackEvent, prescriptionID]);

  const backButton = (
    <Button
      label={orderTypeContext === 'Checkout' ? 'Back to cart' : 'Got it'}
      onPress={closeActionSheet}
    />
  );

  return (
    <ActionSheetV2
      title="A billing specialist is finalizing your price"
      buttons={[backButton]}
      analyticsName="checkout price pending"
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <H3>Why can't I see a price now?</H3>
        <SmSpacing />
        <Body>
          As every prescription has unique billing rules, a billing specialist sometimes needs to step in to guide
          complex cases to get you a final price.
        </Body>
        <LgSpacing />
        {orderTypeContext === 'Add to cart' || orderTypeContext === 'Checkout' ? (
          <>
            <H3>Schedule now to get your price finalized</H3>
            <SmSpacing />
            <Body>
              Once your delivery is scheduled, our team will finalize your price. You’ll be able to review the price
              before shipping.
            </Body>
          </>
        ) : null}
        {orderTypeContext === 'Post checkout' ? (
          <>
            <H3>What to expect next</H3>
            <SmSpacing />
            <Body>Our team will finalize your price. You’ll be able to review the price before shipping.</Body>
          </>
        ) : null}
      </LgPadding>
    </ActionSheetV2>
  );
};
